import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Type, Palette, AlignLeft, Image, Eye, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { invalidatePageBackgrounds } from './PageBackground';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Font options
const FONT_OPTIONS = [
  { value: 'font-heading', label: 'Racing (Default)' },
  { value: 'font-sans', label: 'Sans Serif' },
  { value: 'font-serif', label: 'Serif' },
  { value: 'font-mono', label: 'Monospace' },
];

// Font size options
const FONT_SIZE_OPTIONS = [
  { value: 'text-xs', label: 'Extra Small' },
  { value: 'text-sm', label: 'Small' },
  { value: 'text-base', label: 'Normal' },
  { value: 'text-lg', label: 'Large' },
  { value: 'text-xl', label: 'Extra Large' },
  { value: 'text-2xl', label: '2X Large' },
  { value: 'text-3xl', label: '3X Large' },
  { value: 'text-4xl', label: '4X Large' },
  { value: 'text-5xl', label: '5X Large' },
  { value: 'text-6xl', label: '6X Large' },
];

// Color presets
const COLOR_PRESETS = [
  { value: '#ff4400', label: 'Primary Orange' },
  { value: '#00ff87', label: 'Secondary Green' },
  { value: '#ffffff', label: 'White' },
  { value: '#a1a1aa', label: 'Zinc 400' },
  { value: '#71717a', label: 'Zinc 500' },
  { value: '#ef4444', label: 'Red' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#000000', label: 'Black' },
];

// Collapsible Section Component
function CollapsibleSection({ title, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-zinc-700 mb-4">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-zinc-800 hover:bg-zinc-750 transition-colors"
      >
        <span className="font-heading font-bold text-lg">{title}</span>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {isOpen && (
        <div className="p-4 bg-zinc-900/50">
          {children}
        </div>
      )}
    </div>
  );
}

// Text Editor Field Component
function TextEditorField({ label, value, onChange, placeholder, multiline = false }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-zinc-400 mb-2">{label}</label>
      {multiline ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary p-3 text-white resize-none"
        />
      ) : (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-10 px-3 text-white"
        />
      )}
    </div>
  );
}

// Style Editor Field Component
function StyleEditorField({ label, fontValue, fontSizeValue, colorValue, onFontChange, onFontSizeChange, onColorChange }) {
  return (
    <div className="mb-4 p-4 bg-zinc-800/50 border border-zinc-700">
      <label className="block text-sm font-bold text-zinc-300 mb-3">{label} - Style</label>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {onFontChange && (
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Font Family</label>
            <select
              value={fontValue || 'font-heading'}
              onChange={(e) => onFontChange(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary h-10 px-2 text-white text-sm"
            >
              {FONT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}
        {onFontSizeChange && (
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Font Size</label>
            <select
              value={fontSizeValue || 'text-base'}
              onChange={(e) => onFontSizeChange(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary h-10 px-2 text-white text-sm"
            >
              {FONT_SIZE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}
        {onColorChange && (
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Color</label>
            <div className="flex space-x-2">
              <input
                type="color"
                value={colorValue || '#ffffff'}
                onChange={(e) => onColorChange(e.target.value)}
                className="w-10 h-10 bg-zinc-950 border border-zinc-800 cursor-pointer"
              />
              <select
                value={colorValue || '#ffffff'}
                onChange={(e) => onColorChange(e.target.value)}
                className="flex-1 bg-zinc-950 border border-zinc-800 focus:border-primary h-10 px-2 text-white text-sm"
              >
                <option value="">Custom</option>
                {COLOR_PRESETS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Image Upload Field Component
function ImageUploadField({ label, value, onChange, onUpload }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-zinc-400 mb-2">{label}</label>
      <div className="space-y-3">
        <div className="flex items-center space-x-3">
          <label className="flex items-center space-x-2 bg-primary hover:bg-primary/80 text-white px-4 py-2 cursor-pointer transition-colors">
            <Upload size={16} />
            <span className="text-sm">Upload</span>
            <input
              type="file"
              accept="image/*,.pdf"
              className="hidden"
              onChange={onUpload}
            />
          </label>
          {value && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="text-red-500 hover:text-red-400 text-sm"
            >
              Remove
            </button>
          )}
        </div>
        <input
          type="url"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Or enter URL..."
          className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary h-10 px-3 text-white text-sm"
        />
        {value && (
          <div className="bg-zinc-800 p-2 inline-block">
            <img src={value} alt="Preview" className="h-20 object-contain" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function PageEditor() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('landing');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/system/settings`, { withCredentials: true });
      setSettings(response.data || {});
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleUpload = async (file, fieldName) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      toast.loading('Uploading...');
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.dismiss();
      
      const fileUrl = response.data.url;
      updateSetting(fieldName, fileUrl);
      toast.success('File uploaded!');
    } catch (error) {
      toast.dismiss();
      toast.error('Upload failed');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/system/settings`, settings, { withCredentials: true });
      invalidatePageBackgrounds();
      toast.success('All changes saved!');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'landing', label: 'Landing Page' },
    { id: 'login', label: 'Login Page' },
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'teamselection', label: 'Team Selection' },
    { id: 'leaderboard', label: 'Leaderboard' },
    { id: 'rules', label: 'Rules' },
    { id: 'calendar', label: 'Calendar' },
    { id: 'backgrounds', label: 'Page Backgrounds' },
    { id: 'global', label: 'Global Theme' },
  ];

  return (
    <div className="bg-card border border-zinc-800 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading font-bold text-2xl">
          <Palette className="inline mr-2" size={24} />
          Page Editor
        </h2>
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center space-x-2 bg-primary hover:bg-primary/80 text-white px-6 py-2 font-bold transition-colors disabled:opacity-50"
        >
          <Save size={18} />
          <span>{loading ? 'Saving...' : 'Save All Changes'}</span>
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-zinc-700 pb-4">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-bold transition-colors ${
              activeTab === tab.id
                ? 'bg-primary text-white'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Landing Page Settings */}
      {activeTab === 'landing' && (
        <div>
          <h3 className="font-heading font-bold text-xl text-primary mb-4">Landing Page Settings</h3>
          
          <CollapsibleSection title="Hero Section" defaultOpen={true}>
            <ImageUploadField
              label="Main Logo (replaces title text)"
              value={settings.landing_page_logo}
              onChange={(v) => updateSetting('landing_page_logo', v)}
              onUpload={(e) => handleUpload(e.target.files[0], 'landing_page_logo')}
            />
            <TextEditorField
              label="Title Text (shown if no logo)"
              value={settings.landing_page_title}
              onChange={(v) => updateSetting('landing_page_title', v)}
              placeholder="MSF RACING"
            />
            <StyleEditorField
              label="Title"
              fontValue={settings.landing_title_font}
              fontSizeValue={settings.landing_title_size}
              colorValue={settings.landing_title_color}
              onFontChange={(v) => updateSetting('landing_title_font', v)}
              onFontSizeChange={(v) => updateSetting('landing_title_size', v)}
              onColorChange={(v) => updateSetting('landing_title_color', v)}
            />
            
            <ImageUploadField
              label="Subtitle Logo (replaces subtitle text)"
              value={settings.landing_page_subtitle_logo}
              onChange={(v) => updateSetting('landing_page_subtitle_logo', v)}
              onUpload={(e) => handleUpload(e.target.files[0], 'landing_page_subtitle_logo')}
            />
            <TextEditorField
              label="Subtitle Text (shown if no logo)"
              value={settings.landing_page_subtitle}
              onChange={(v) => updateSetting('landing_page_subtitle', v)}
              placeholder="FANTASY LEAGUE"
            />
            <StyleEditorField
              label="Subtitle"
              fontValue={settings.landing_subtitle_font}
              fontSizeValue={settings.landing_subtitle_size}
              colorValue={settings.landing_subtitle_color}
              onFontChange={(v) => updateSetting('landing_subtitle_font', v)}
              onFontSizeChange={(v) => updateSetting('landing_subtitle_size', v)}
              onColorChange={(v) => updateSetting('landing_subtitle_color', v)}
            />
            
            <TextEditorField
              label="Description"
              value={settings.landing_page_description}
              onChange={(v) => updateSetting('landing_page_description', v)}
              placeholder="Build your dream team..."
              multiline
            />
            <StyleEditorField
              label="Description"
              fontSizeValue={settings.landing_desc_size}
              colorValue={settings.landing_desc_color}
              onFontSizeChange={(v) => updateSetting('landing_desc_size', v)}
              onColorChange={(v) => updateSetting('landing_desc_color', v)}
            />
          </CollapsibleSection>

          <CollapsibleSection title="Background">
            <ImageUploadField
              label="Background Image"
              value={settings.landing_page_image}
              onChange={(v) => updateSetting('landing_page_image', v)}
              onUpload={(e) => handleUpload(e.target.files[0], 'landing_page_image')}
            />
            <div className="mb-4">
              <label className="block text-sm font-medium text-zinc-400 mb-2">Background Overlay Opacity</label>
              <input
                type="range"
                min="0"
                max="100"
                value={settings.landing_overlay_opacity || 70}
                onChange={(e) => updateSetting('landing_overlay_opacity', parseInt(e.target.value))}
                className="w-full"
              />
              <span className="text-sm text-zinc-500">{settings.landing_overlay_opacity || 70}%</span>
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Get Started Button">
            <TextEditorField
              label="Button Text"
              value={settings.landing_button_text}
              onChange={(v) => updateSetting('landing_button_text', v)}
              placeholder="Get Started"
            />
            <StyleEditorField
              label="Button"
              colorValue={settings.landing_button_color}
              onColorChange={(v) => updateSetting('landing_button_color', v)}
            />
          </CollapsibleSection>

          <CollapsibleSection title="Footer">
            <TextEditorField
              label="Footer Text"
              value={settings.landing_footer_text}
              onChange={(v) => updateSetting('landing_footer_text', v)}
              placeholder="© 2025 MSF Fantasy League"
            />
          </CollapsibleSection>
        </div>
      )}

      {/* Login Page Settings */}
      {activeTab === 'login' && (
        <div>
          <h3 className="font-heading font-bold text-xl text-primary mb-4">Login Page Settings</h3>
          
          <CollapsibleSection title="Branding" defaultOpen={true}>
            <ImageUploadField
              label="Login Page Logo"
              value={settings.login_page_logo}
              onChange={(v) => updateSetting('login_page_logo', v)}
              onUpload={(e) => handleUpload(e.target.files[0], 'login_page_logo')}
            />
            <TextEditorField
              label="Title"
              value={settings.login_page_title}
              onChange={(v) => updateSetting('login_page_title', v)}
              placeholder="MSF RACING"
            />
            <TextEditorField
              label="Subtitle"
              value={settings.login_page_subtitle}
              onChange={(v) => updateSetting('login_page_subtitle', v)}
              placeholder="Fantasy League"
            />
          </CollapsibleSection>

          <CollapsibleSection title="Form Labels">
            <TextEditorField
              label="Welcome Text (Login Tab)"
              value={settings.login_page_welcome_text}
              onChange={(v) => updateSetting('login_page_welcome_text', v)}
              placeholder="Welcome Back"
            />
            <TextEditorField
              label="Register Text (Register Tab)"
              value={settings.login_page_register_text}
              onChange={(v) => updateSetting('login_page_register_text', v)}
              placeholder="Create Account"
            />
            <TextEditorField
              label="Login Button Text"
              value={settings.login_button_text}
              onChange={(v) => updateSetting('login_button_text', v)}
              placeholder="Sign In"
            />
            <TextEditorField
              label="Register Button Text"
              value={settings.register_button_text}
              onChange={(v) => updateSetting('register_button_text', v)}
              placeholder="Create Account"
            />
          </CollapsibleSection>
        </div>
      )}

      {/* Dashboard Settings */}
      {activeTab === 'dashboard' && (
        <div>
          <h3 className="font-heading font-bold text-xl text-primary mb-4">Dashboard Settings</h3>
          
          <CollapsibleSection title="Welcome Section" defaultOpen={true}>
            <TextEditorField
              label="Welcome Prefix"
              value={settings.dashboard_welcome_prefix}
              onChange={(v) => updateSetting('dashboard_welcome_prefix', v)}
              placeholder="WELCOME BACK,"
            />
            <StyleEditorField
              label="Welcome Text"
              fontSizeValue={settings.dashboard_welcome_size}
              colorValue={settings.dashboard_welcome_color}
              onFontSizeChange={(v) => updateSetting('dashboard_welcome_size', v)}
              onColorChange={(v) => updateSetting('dashboard_welcome_color', v)}
            />
            <TextEditorField
              label="Subtitle"
              value={settings.dashboard_subtitle}
              onChange={(v) => updateSetting('dashboard_subtitle', v)}
              placeholder="Your fantasy racing command center"
            />
          </CollapsibleSection>

          <CollapsibleSection title="Garage Section">
            <TextEditorField
              label="Garage Title"
              value={settings.dashboard_garage_title}
              onChange={(v) => updateSetting('dashboard_garage_title', v)}
              placeholder="MY GARAGE"
            />
            <TextEditorField
              label="Edit Team Button Text"
              value={settings.dashboard_edit_button_text}
              onChange={(v) => updateSetting('dashboard_edit_button_text', v)}
              placeholder="Edit Team"
            />
            <TextEditorField
              label="No Selection Message"
              value={settings.dashboard_no_selection_text}
              onChange={(v) => updateSetting('dashboard_no_selection_text', v)}
              placeholder="You haven't selected your team yet!"
            />
          </CollapsibleSection>

          <CollapsibleSection title="Tab Labels">
            <TextEditorField
              label="Racing Tab Label"
              value={settings.dashboard_racing_tab_label}
              onChange={(v) => updateSetting('dashboard_racing_tab_label', v)}
              placeholder="MSF Racing"
            />
            <TextEditorField
              label="Moto Tab Label"
              value={settings.dashboard_moto_tab_label}
              onChange={(v) => updateSetting('dashboard_moto_tab_label', v)}
              placeholder="MotoMSF"
            />
          </CollapsibleSection>
        </div>
      )}

      {/* Team Selection Settings */}
      {activeTab === 'teamselection' && (
        <div>
          <h3 className="font-heading font-bold text-xl text-primary mb-4">Team Selection Settings</h3>
          
          <CollapsibleSection title="Racing Team Selection" defaultOpen={true}>
            <TextEditorField
              label="Page Title"
              value={settings.team_selection_title}
              onChange={(v) => updateSetting('team_selection_title', v)}
              placeholder="BUILD YOUR GARAGE"
            />
            <TextEditorField
              label="Subtitle"
              value={settings.team_selection_subtitle}
              onChange={(v) => updateSetting('team_selection_subtitle', v)}
              placeholder="Select 2 drivers, 3 cars, and 1 tuner."
            />
            <TextEditorField
              label="Drivers Section Title"
              value={settings.team_drivers_title}
              onChange={(v) => updateSetting('team_drivers_title', v)}
              placeholder="SELECT DRIVERS"
            />
            <TextEditorField
              label="Cars Section Title"
              value={settings.team_cars_title}
              onChange={(v) => updateSetting('team_cars_title', v)}
              placeholder="SELECT CARS"
            />
            <TextEditorField
              label="Tuner Section Title"
              value={settings.team_tuner_title}
              onChange={(v) => updateSetting('team_tuner_title', v)}
              placeholder="SELECT TUNER"
            />
            <TextEditorField
              label="Save Button Text"
              value={settings.team_save_button_text}
              onChange={(v) => updateSetting('team_save_button_text', v)}
              placeholder="Save Team"
            />
          </CollapsibleSection>

          <CollapsibleSection title="Moto Team Selection">
            <TextEditorField
              label="Page Title"
              value={settings.moto_selection_title}
              onChange={(v) => updateSetting('moto_selection_title', v)}
              placeholder="BUILD YOUR GARAGE"
            />
            <TextEditorField
              label="Subtitle"
              value={settings.moto_selection_subtitle}
              onChange={(v) => updateSetting('moto_selection_subtitle', v)}
              placeholder="Select 2 riders, 3 bikes, and 1 tuner."
            />
            <TextEditorField
              label="Riders Section Title"
              value={settings.moto_riders_title}
              onChange={(v) => updateSetting('moto_riders_title', v)}
              placeholder="SELECT RIDERS"
            />
            <TextEditorField
              label="Bikes Section Title"
              value={settings.moto_bikes_title}
              onChange={(v) => updateSetting('moto_bikes_title', v)}
              placeholder="SELECT BIKES"
            />
          </CollapsibleSection>

          <CollapsibleSection title="Budget Display">
            <TextEditorField
              label="Budget Label"
              value={settings.budget_label}
              onChange={(v) => updateSetting('budget_label', v)}
              placeholder="Total Budget"
            />
            <TextEditorField
              label="Spent Label"
              value={settings.spent_label}
              onChange={(v) => updateSetting('spent_label', v)}
              placeholder="Spent"
            />
            <TextEditorField
              label="Remaining Label"
              value={settings.remaining_label}
              onChange={(v) => updateSetting('remaining_label', v)}
              placeholder="Remaining"
            />
          </CollapsibleSection>
        </div>
      )}

      {/* Leaderboard Settings */}
      {activeTab === 'leaderboard' && (
        <div>
          <h3 className="font-heading font-bold text-xl text-primary mb-4">Leaderboard Settings</h3>
          
          <CollapsibleSection title="Header" defaultOpen={true}>
            <TextEditorField
              label="Page Title"
              value={settings.leaderboard_title}
              onChange={(v) => updateSetting('leaderboard_title', v)}
              placeholder="LEADERBOARD"
            />
            <StyleEditorField
              label="Title"
              fontSizeValue={settings.leaderboard_title_size}
              colorValue={settings.leaderboard_title_color}
              onFontSizeChange={(v) => updateSetting('leaderboard_title_size', v)}
              onColorChange={(v) => updateSetting('leaderboard_title_color', v)}
            />
            <TextEditorField
              label="Subtitle"
              value={settings.leaderboard_subtitle}
              onChange={(v) => updateSetting('leaderboard_subtitle', v)}
              placeholder="See how you stack up against the competition"
            />
          </CollapsibleSection>

          <CollapsibleSection title="Table Headers">
            <TextEditorField
              label="Rank Column"
              value={settings.leaderboard_rank_header}
              onChange={(v) => updateSetting('leaderboard_rank_header', v)}
              placeholder="RANK"
            />
            <TextEditorField
              label="Player Column"
              value={settings.leaderboard_player_header}
              onChange={(v) => updateSetting('leaderboard_player_header', v)}
              placeholder="PLAYER"
            />
            <TextEditorField
              label="Points Column"
              value={settings.leaderboard_points_header}
              onChange={(v) => updateSetting('leaderboard_points_header', v)}
              placeholder="POINTS"
            />
            <TextEditorField
              label="Penalty Column"
              value={settings.leaderboard_penalty_header}
              onChange={(v) => updateSetting('leaderboard_penalty_header', v)}
              placeholder="PENALTY"
            />
          </CollapsibleSection>

          <CollapsibleSection title="Empty State">
            <TextEditorField
              label="No Players Message"
              value={settings.leaderboard_empty_text}
              onChange={(v) => updateSetting('leaderboard_empty_text', v)}
              placeholder="No players on the leaderboard yet!"
            />
          </CollapsibleSection>
        </div>
      )}

      {/* Rules Settings */}
      {activeTab === 'rules' && (
        <div>
          <h3 className="font-heading font-bold text-xl text-primary mb-4">Rules Page Settings</h3>
          
          <CollapsibleSection title="Section Titles" defaultOpen={true}>
            <TextEditorField
              label="Main Title"
              value={settings.rules_main_title}
              onChange={(v) => updateSetting('rules_main_title', v)}
              placeholder="RULES & SCORING"
            />
            <TextEditorField
              label="Team Selection Title"
              value={settings.rules_team_title}
              onChange={(v) => updateSetting('rules_team_title', v)}
              placeholder="Team Selection"
            />
            <TextEditorField
              label="Points System Title"
              value={settings.rules_points_title}
              onChange={(v) => updateSetting('rules_points_title', v)}
              placeholder="Points System"
            />
            <TextEditorField
              label="Penalties Title"
              value={settings.rules_penalties_title}
              onChange={(v) => updateSetting('rules_penalties_title', v)}
              placeholder="Penalties"
            />
          </CollapsibleSection>

          <CollapsibleSection title="Rules Content">
            <TextEditorField
              label="Racing Rules"
              value={settings.rules_racing_text}
              onChange={(v) => updateSetting('rules_racing_text', v)}
              placeholder="Select 2 Drivers, 3 Cars, and 1 Tuner within 200,000 credits"
              multiline
            />
            <TextEditorField
              label="Moto Rules"
              value={settings.rules_moto_text}
              onChange={(v) => updateSetting('rules_moto_text', v)}
              placeholder="Select 2 Riders, 3 Bikes, and 1 Tuner within 150,000 credits"
              multiline
            />
            <TextEditorField
              label="Transfer Window Rules"
              value={settings.rules_transfer_text}
              onChange={(v) => updateSetting('rules_transfer_text', v)}
              placeholder="Players are only allowed to change their lineup..."
              multiline
            />
            <TextEditorField
              label="Penalty Text"
              value={settings.rules_penalty_text}
              onChange={(v) => updateSetting('rules_penalty_text', v)}
              placeholder="-50 points penalty for changing your team selection"
            />
          </CollapsibleSection>
        </div>
      )}

      {/* Calendar Settings */}
      {activeTab === 'calendar' && (
        <div>
          <h3 className="font-heading font-bold text-xl text-primary mb-4">Calendar Settings</h3>
          
          <CollapsibleSection title="Header" defaultOpen={true}>
            <TextEditorField
              label="Calendar Title"
              value={settings.calendar_title}
              onChange={(v) => updateSetting('calendar_title', v)}
              placeholder="RACE CALENDAR 2025"
            />
            <TextEditorField
              label="Venue Name"
              value={settings.calendar_venue}
              onChange={(v) => updateSetting('calendar_venue', v)}
              placeholder="Sepang International Circuit"
            />
            <TextEditorField
              label="Footer Message"
              value={settings.calendar_footer_text}
              onChange={(v) => updateSetting('calendar_footer_text', v)}
              placeholder="Make sure to lock in your teams before playing!"
            />
          </CollapsibleSection>

          <CollapsibleSection title="Race Dates">
            <TextEditorField
              label="Round 1 Dates"
              value={settings.calendar_round1}
              onChange={(v) => updateSetting('calendar_round1', v)}
              placeholder="16-17 May"
            />
            <TextEditorField
              label="Round 2 Dates"
              value={settings.calendar_round2}
              onChange={(v) => updateSetting('calendar_round2', v)}
              placeholder="4-5 July"
            />
            <TextEditorField
              label="Round 3 Dates"
              value={settings.calendar_round3}
              onChange={(v) => updateSetting('calendar_round3', v)}
              placeholder="29-30 August"
            />
            <TextEditorField
              label="Round 4 Dates"
              value={settings.calendar_round4}
              onChange={(v) => updateSetting('calendar_round4', v)}
              placeholder="17-18 October"
            />
            <TextEditorField
              label="Round 5 Dates"
              value={settings.calendar_round5}
              onChange={(v) => updateSetting('calendar_round5', v)}
              placeholder="5-6 December"
            />
          </CollapsibleSection>
        </div>
      )}

      {/* Page Backgrounds Settings */}
      {activeTab === 'backgrounds' && (
        <div>
          <h3 className="font-heading font-bold text-xl text-primary mb-4">Page Backgrounds</h3>
          <p className="text-sm text-zinc-400 mb-6">Set background images and overlay opacity for each player-facing page. Images will appear behind content with a dark overlay.</p>
          
          {[
            { key: 'landing', label: 'Landing Page' },
            { key: 'login', label: 'Login Page' },
            { key: 'dashboard', label: 'Dashboard' },
            { key: 'leaderboard', label: 'Leaderboard' },
            { key: 'team_selection', label: 'Team Selection (Racing)' },
            { key: 'moto_selection', label: 'Team Selection (Moto)' },
            { key: 'workshop', label: 'Workshop' },
            { key: 'results', label: 'Race Results' },
            { key: 'rules', label: 'Rules' },
            { key: 'round_results', label: 'Round Results / Standings' },
            { key: 'settings', label: 'Settings' },
          ].map(page => (
            <CollapsibleSection key={page.key} title={page.label} defaultOpen={false}>
              <ImageUploadField
                label="Background Image"
                value={settings[`page_bg_${page.key}`]}
                onChange={(v) => updateSetting(`page_bg_${page.key}`, v)}
                onUpload={(e) => handleUpload(e.target.files[0], `page_bg_${page.key}`)}
              />
              <div className="mt-3">
                <label className="block text-sm text-zinc-400 mb-1">Overlay Opacity: {settings[`page_overlay_${page.key}`] || 70}%</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={settings[`page_overlay_${page.key}`] || 70}
                  onChange={(e) => updateSetting(`page_overlay_${page.key}`, parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-zinc-600 mt-1">
                  <span>Transparent</span>
                  <span>Full Dark</span>
                </div>
              </div>
              {settings[`page_bg_${page.key}`] && (
                <div className="mt-3 relative h-24 overflow-hidden border border-zinc-700">
                  <img src={settings[`page_bg_${page.key}`]?.startsWith('http') ? settings[`page_bg_${page.key}`] : `${BACKEND_URL}${settings[`page_bg_${page.key}`]}`} alt="Preview" className="w-full h-full object-cover" />
                  <div style={{ backgroundColor: `rgba(0,0,0,${(settings[`page_overlay_${page.key}`] || 70) / 100})` }} className="absolute inset-0" />
                  <p className="absolute inset-0 flex items-center justify-center text-white font-heading font-bold text-sm">{page.label} Preview</p>
                </div>
              )}
            </CollapsibleSection>
          ))}
        </div>
      )}


      {/* Global Theme Settings */}
      {activeTab === 'global' && (
        <div>
          <h3 className="font-heading font-bold text-xl text-primary mb-4">Global Theme Settings</h3>
          
          <CollapsibleSection title="Navbar Logo" defaultOpen={true}>
            <ImageUploadField
              label="Navbar Logo (replaces 'MSF RACING' text in navigation bar)"
              value={settings.navbar_logo}
              onChange={(v) => updateSetting('navbar_logo', v)}
              onUpload={(e) => handleUpload(e.target.files[0], 'navbar_logo')}
            />
            <p className="text-xs text-zinc-500 mt-1">Upload your MSF logo here. It will appear in the top-left of every page's navigation bar.</p>
          </CollapsibleSection>

          <CollapsibleSection title="Primary Colors" defaultOpen={false}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Primary Color (Orange)</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={settings.theme_primary_color || '#ff4400'}
                    onChange={(e) => updateSetting('theme_primary_color', e.target.value)}
                    className="w-12 h-10 bg-zinc-950 border border-zinc-800 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.theme_primary_color || '#ff4400'}
                    onChange={(e) => updateSetting('theme_primary_color', e.target.value)}
                    className="flex-1 bg-zinc-950 border border-zinc-800 h-10 px-3 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Secondary Color (Green)</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={settings.theme_secondary_color || '#00ff87'}
                    onChange={(e) => updateSetting('theme_secondary_color', e.target.value)}
                    className="w-12 h-10 bg-zinc-950 border border-zinc-800 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.theme_secondary_color || '#00ff87'}
                    onChange={(e) => updateSetting('theme_secondary_color', e.target.value)}
                    className="flex-1 bg-zinc-950 border border-zinc-800 h-10 px-3 text-white"
                  />
                </div>
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Background Colors">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Background Color</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={settings.theme_bg_color || '#0a0a0a'}
                    onChange={(e) => updateSetting('theme_bg_color', e.target.value)}
                    className="w-12 h-10 bg-zinc-950 border border-zinc-800 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.theme_bg_color || '#0a0a0a'}
                    onChange={(e) => updateSetting('theme_bg_color', e.target.value)}
                    className="flex-1 bg-zinc-950 border border-zinc-800 h-10 px-3 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Card Color</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={settings.theme_card_color || '#18181b'}
                    onChange={(e) => updateSetting('theme_card_color', e.target.value)}
                    className="w-12 h-10 bg-zinc-950 border border-zinc-800 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.theme_card_color || '#18181b'}
                    onChange={(e) => updateSetting('theme_card_color', e.target.value)}
                    className="flex-1 bg-zinc-950 border border-zinc-800 h-10 px-3 text-white"
                  />
                </div>
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Typography">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Default Heading Font</label>
              <select
                value={settings.theme_heading_font || 'font-heading'}
                onChange={(e) => updateSetting('theme_heading_font', e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 h-10 px-3 text-white"
              >
                {FONT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-zinc-400 mb-2">Default Body Font</label>
              <select
                value={settings.theme_body_font || 'font-sans'}
                onChange={(e) => updateSetting('theme_body_font', e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 h-10 px-3 text-white"
              >
                {FONT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </CollapsibleSection>
        </div>
      )}

      {/* Save Button at Bottom */}
      <div className="mt-8 pt-6 border-t border-zinc-700">
        <button
          onClick={handleSave}
          disabled={loading}
          className="w-full flex items-center justify-center space-x-2 bg-primary hover:bg-primary/80 text-white py-4 font-bold text-lg transition-colors disabled:opacity-50"
        >
          <Save size={24} />
          <span>{loading ? 'Saving All Changes...' : 'Save All Changes'}</span>
        </button>
        <p className="text-center text-zinc-500 text-sm mt-2">
          Changes will be visible to all users immediately after saving
        </p>
      </div>
    </div>
  );
}
